import React, { useState, useEffect, ChangeEvent } from "react";
import axios from "axios";

import { Button } from "../Components/ui/button";
import { Input } from "../Components/ui/input";
import { Checkbox } from "../Components/ui/checkbox";
import { Globe } from "lucide-react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../Components/ui/select";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../Components/ui/card";

import { Label } from "../Components/ui/label";
import { useParams } from "react-router-dom";
import { CirclePause, CircleX, Check } from "lucide-react";

// ==========================
// STEP ENUM
// ==========================

enum Step {
  Intro = 1,
  Verify,
  Type,
  Reason,
  Confirm,
  Success,
  InvalidLink,
}

// ==========================
// TYPES
// ==========================

interface FormDataType {
  countryCode: string;
  phoneNumber: string;
  unsubscribeType: "trip" | "permanent" | "";
  reasons: string[];
  otherReason: string; // NEW
}

interface CountryCode {
  label: string;

  value: string;

  dialCode: string;
}

// ==========================
// COMPONENT
// ==========================

const Unsubscribe: React.FC = () => {
  const { unique_id } = useParams();

  const [step, setStep] = useState<Step>(Step.Intro);

  const [loading, setLoading] = useState(false);

  const [pageInitializing, setPageInitializing] = useState(true);

  const [partnerName, setPartnerName] = useState("");

  const [partnerId, setPartnerId] = useState("");

  const [countryCodes, setCountryCodes] = useState<CountryCode[]>([]);

  const [countryCodesLoading, setCountryCodesLoading] = useState(false);

  const [apiUrl, setApiUrl] = useState("");

  const [optoutTypeFromServer, setOptoutTypeFromServer] = useState<
    "trip" | "permanent" | ""
  >("");

  const [formData, setFormData] = useState<FormDataType>({
    countryCode: "1",
    phoneNumber: "",
    unsubscribeType: "",
    reasons: [],
    otherReason: "", // NEW
  });
  const [error, setError] = useState("");

  const [unsubscribeReasons, setUnsubscribeReasons] = useState<string[]>([]);

  // const unsubscribeReasons = [
  //   "Not interested",
  //   "Prefer not receive promotions",
  //   "Stop",
  //   "Block",
  //   "Other", // NEW
  // ];

  // ==========================
  // LOAD CONFIG + PARTNER
  // ==========================

  const [OperatorUrl, setOperatorUrl] = useState("");
  const [OptOutWebhookUrl, SetOptOutWebhookUrl] = useState("");


const trackPageVisit = async (webhookUrl: string) => {
  try {
    let clientIp = "";
    try {
      const ipRes = await fetch("https://ipapi.co/json/");
      const ipData = await ipRes.json();
      clientIp = ipData.ip;
    } catch {
      clientIp = "unknown";
    }

    await axios.post(`${webhookUrl}/abc12345`, {
      unique_id,
      link_clicked: true,
      optoutMode: "Portal",
      ipAddress: clientIp,
    });
  } catch {
    // Silently fail
  }
};
  
  useEffect(() => {
    fetch("/config.json")
      .then((res) => res.json())
      .then((config) => {
        setOperatorUrl(config.OperatorUrl);
        SetOptOutWebhookUrl(config.OptOutWebhookUrl)
        setApiUrl(config.ApiUrlAdvAcc);
        // ✅ Add this line
        //setUnsubscribeReasons(config.unsubscribeReasons || []);

        trackPageVisit(config.OptOutWebhookUrl);
      });
  }, []);

  useEffect(() => {
    if (!OperatorUrl || !unique_id) return;

    checkOptoutStatus();

    fetchPartner();

    fetchCountryCodes();
  }, [OperatorUrl, apiUrl, unique_id]);

  useEffect(() => {
  if (apiUrl) {
    fetchUnsubscribeReasons();
  }
}, [OperatorUrl]);

const fetchUnsubscribeReasons = async () => {
  try {
    const res = await axios.get(`${OperatorUrl}/GetSmsOptoutContacts`);

    console.log("API RESPONSE:", res.data);

    if (res.data?.status === "Success" && Array.isArray(res.data?.data)) {
      const reasons = res.data.data.map((item: any) =>
        item.reason.charAt(0).toUpperCase() + item.reason.slice(1)
      );

      setUnsubscribeReasons(reasons);
    } else {
      console.warn("Unexpected API structure", res.data);
      setUnsubscribeReasons([]);
    }
  } catch (error) {
    console.error("Failed to fetch unsubscribe reasons", error);
    setUnsubscribeReasons([]);
  }
};

  const fetchCountryCodes = async () => {
    try {
      setCountryCodesLoading(true);

      const response = await axios.get(`${apiUrl}/GetAllCountryCodes`);

      if (response.data.status === "Success") {
        const formatted = response.data.country_codes.map((cc: CountryCode) => {
          const iso = cc.label.split(" ")[0].toUpperCase();

          const dialCode = cc.value;

          return {
            label: `${iso} (+${dialCode})`,

            value: `${iso}_${dialCode}`, // UNIQUE value

            dialCode: dialCode, // real dial code
          };
        });

        setCountryCodes(formatted);

        // default to US
        const us = formatted.find((c: { label: string }) =>
          c.label.startsWith("US"),
        );

        if (us) {
          setFormData((prev) => ({
            ...prev,
            countryCode: us.value,
          }));
        }
      }
    } finally {
      setCountryCodesLoading(false);
    }
  };
  const fetchPartner = async () => {
    try {
      setLoading(true);

      const res = await axios.get(`${OperatorUrl}/GetPartnerByOptoutRef`, {
        params: { optoutRefId: unique_id },
      });

      if (res.data.status === "Success") {
        setPartnerName(res.data.partner.partner_name);
        setPartnerId(res.data.partner.partner_id);
      } else {
        setStep(Step.InvalidLink);
      }
    } catch {
      setStep(Step.InvalidLink);
    } finally {
      setLoading(false);
    }
  };

  const checkOptoutStatus = async () => {
    debugger;
    try {
      const res = await axios.get(`${OperatorUrl}/CheckTripOptoutStatus`, {
        params: { optoutRefId: unique_id },
      });

      if (res.data.status === "Success" && res.data.alreadyOptedOut === true) {
        setOptoutTypeFromServer(res.data.optoutType);

        setStep(Step.Success); // or setStep
      }
    } finally {
      setPageInitializing(false);
    }
  };
  // ==========================
  // VERIFY NUMBER API
  // ==========================

  const verifyNumber = async () => {
    if (!formData.phoneNumber) {
      setError("Enter phone number");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const selectedCountry = countryCodes.find(
        (c) => c.value === formData.countryCode,
      );

      const msisdn = selectedCountry!.dialCode + formData.phoneNumber;

      const res = await axios.post(`${OperatorUrl}/VerifyOptoutMsisdn`, {
        uniqueId: unique_id,
        msisdn,
      });

      if (res.data.status === "Success") {
        setStep(Step.Type);
      } else {
        setError("This number does not match unsubscribe link");
      }
    } catch {
      setError("Verification failed");
    } finally {
      setLoading(false);
    }
  };

  // ==========================
  // FINAL UNSUBSCRIBE API
  // ==========================

const confirmUnsubscribe = async () => {
  try {
    setLoading(true);

    const selectedCountry = countryCodes.find(
      (c) => c.value === formData.countryCode
    );

    const msisdn = selectedCountry!.dialCode + formData.phoneNumber;

    // ===== GET CLIENT IP =====
    let clientIp = "";

    try {
      const ipRes = await fetch("https://ipapi.co/json/");
      const ipData = await ipRes.json();
      clientIp = ipData.ip;
    } catch {
      clientIp = "unknown";
    }

    const finalReasons = formData.reasons.filter((r) => r !== "Other");

    if (formData.otherReason.trim()) {
      finalReasons.push(formData.otherReason);
    }

    const response = await axios.post(`${OptOutWebhookUrl}/abc12345`, {
      unique_id,
      partnerId,
      msisdn,
      optoutReasons: finalReasons,
      optoutMode: "Portal",
      optoutType: formData.unsubscribeType,
      ipAddress: clientIp,
    });

    if (response.data?.status === "ok") {
  setStep(Step.Success);
} else {
  setError("Unexpected response from server");
}
  } catch {
    setError("Failed to unsubscribe");
  } finally {
    setLoading(false);
  }
};

  // ==========================
  // UI HELPERS
  // ==========================

  const back = () => {
    if (step === Step.Verify) setStep(Step.Intro);
    else if (step === Step.Type) setStep(Step.Verify);
    else if (step === Step.Reason) setStep(Step.Type);
    //else if (step === Step.Confirm) setStep(Step.Reason);
    else if (step === Step.Confirm) setStep(Step.Type);
  };

  const toggleReason = (reason: string) => {
    setFormData((prev) => ({
      ...prev,

      reasons: prev.reasons.includes(reason)
        ? prev.reasons.filter((r) => r !== reason)
        : [...prev.reasons, reason],
    }));
  };

  // ==========================
  // STEP UI
  // ==========================

  const renderStep = () => {
    if (pageInitializing)
      return (
        <CardContent className="text-center py-8 text-sm">
          Loading...
        </CardContent>
      );

    if (loading)
      return (
        <CardContent className="text-center py-8 text-sm">
          Processing...
        </CardContent>
      );

    switch (step) {
      // ======================
      // INTRO (CENTERED)
      // ======================
      case Step.Intro:
        return (
          <CardContent className="px-5 pb-6 text-left">
            <CardTitle className="text-base font-semibold">
              Manage your travel SMS notifications
            </CardTitle>

            <CardDescription className="text-sm text-left text-gray-500 mt-3">
              You're receiving SMS updates for your current trip. You can stop
              messages for this trip only or unsubscribe permanently from all
              future travel alerts.
            </CardDescription>

            <Button
              className="w-full mt-6 h-10 text-sm"
              onClick={() => setStep(Step.Verify)}
            >
              Continue
            </Button>
          </CardContent>
        );

      // ======================
      // VERIFY
      // ======================
      case Step.Verify:
        return (
          <CardContent className="px-5 pb-6">
            <CardTitle className="text-base text-left mt-8 font-semibold">
              Verify your phone number
            </CardTitle>

            <CardDescription className="text-sm text-left text-gray-500 mt-1">
              Enter the number received by sms to continue
            </CardDescription>

            <div className="mt-8">
              <Label className="text-sm text-left block mb-2">
                Phone number
              </Label>

              <div className="flex gap-2">
                <Select
                  value={formData.countryCode}
                  onValueChange={(v) =>
                    setFormData({ ...formData, countryCode: v })
                  }
                >
                  <SelectTrigger className="w-[110px] h-10 text-sm">
                    <SelectValue />
                  </SelectTrigger>

                  <SelectContent>
                    {countryCodes.map((cc) => (
                      <SelectItem key={cc.value} value={cc.value}>
                        {cc.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Input
                  className="h-10 text-sm"
                  value={formData.phoneNumber}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      phoneNumber: e.target.value.replace(/\D/g, ""),
                    })
                  }
                  placeholder="e.g. 7911 12345"
                />
              </div>
            </div>

            {error && <p className="text-red-500 text-xs mt-2">{error}</p>}

            <Button className="w-full mt-6 h-10 text-sm" onClick={verifyNumber}>
              Verify number
            </Button>
          </CardContent>
        );

      // ======================
      // TYPE
      // ======================
      case Step.Type:
        return (
          <CardContent className="px-5 pb-6 pt-6">
            <CardTitle className="text-base text-left font-semibold">
              How would you like to unsubscribe ?
            </CardTitle>

            <CardDescription className="text-sm text-left text-gray-500 mt-1">
              Choose one of the options below
            </CardDescription>

            <div className="mt-4 space-y-3">
              {/* ===== TRIP OPTION ===== */}
              <div
                onClick={() =>
                  setFormData({ ...formData, unsubscribeType: "trip" })
                }
                className={`flex items-center justify-between border rounded-lg p-4 cursor-pointer transition
      ${
        formData.unsubscribeType === "trip"
          ? "border-blue-500 bg-blue-50"
          : "border-gray-200"
      }`}
              >
                {/* LEFT SECTION */}
                <div className="flex items-start gap-3 flex-1">
                  <div className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center">
                    <CirclePause className="h-6 w-6 text-gray-400" />
                  </div>

                  <div>
                    <p className="text-sm text-left font-medium">
                      Stop messages for this trip only
                    </p>
                    <p className="text-xs text-left text-gray-500 mt-1">
                      You will still receive SMS updates on future trips
                    </p>
                  </div>
                </div>

                {/* RIGHT RADIO */}
                <div
                  className={`h-5 w-5 flex-shrink-0 ml-3 rounded-full border flex items-center justify-center
  ${formData.unsubscribeType === "trip" ? "border-black" : "border-gray-400"}`}
                >
                  {formData.unsubscribeType === "trip" && (
                    <Check className="h-3 w-3 text-black" />
                  )}
                </div>
              </div>

              {/* ===== PERMANENT OPTION ===== */}
              <div
                onClick={() =>
                  setFormData({ ...formData, unsubscribeType: "permanent" })
                }
                className={`flex items-center justify-between border rounded-lg p-4 cursor-pointer transition
      ${
        formData.unsubscribeType === "permanent"
          ? "border-blue-500 bg-blue-50"
          : "border-gray-200"
      }`}
              >
                {/* LEFT SECTION */}
                <div className="flex items-start gap-3 flex-1">
                  <div className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center">
                    <CircleX className="h-6 w-6 text-gray-400" />
                  </div>

                  <div>
                    <p className="text-sm text-left font-medium">
                      Unsubscribe permanently
                    </p>
                    <p className="text-xs text-left text-gray-500 mt-1">
                      You won’t receive any future travel sms alerts
                    </p>
                  </div>
                </div>

                {/* RIGHT RADIO */}
                <div
                  className={`h-5 w-5 flex-shrink-0 ml-3 rounded-full border flex items-center justify-center
  ${
    formData.unsubscribeType === "permanent"
      ? "border-black"
      : "border-gray-400"
  }`}
                >
                  {formData.unsubscribeType === "permanent" && (
                    <Check className="h-3 w-3 text-black" />
                  )}
                </div>
              </div>
            </div>

            <Button
              className="w-full mt-6 h-10 text-sm"
              disabled={!formData.unsubscribeType}
              onClick={() => setStep(Step.Reason)}
            >
              Continue
            </Button>
          </CardContent>
        );

      // ======================
      // REASON (ADDED BACK)
      // ======================
      case Step.Reason:
        return (
          <CardContent className="px-5 pb-6">
            <CardTitle className="text-base text-left pt-6 font-semibold">
              What is your reason for unsubscribing ?
            </CardTitle>
            <CardDescription className="text-xs text-left ">
              Choose one of the options below
            </CardDescription>
            {/* ====================== */}
            {/* REASON STEP */}
            {/* ====================== */}

            <div className="mt-4 space-y-3">
              {unsubscribeReasons
                .filter((r) => r !== "Other")
                .map((reason) => (
                  <div
                    key={reason}
                    onClick={() =>
                      setFormData({
                        ...formData,
                        reasons: [reason], // single selection
                      })
                    }
                    className={`flex items-center justify-between border rounded-lg p-4 cursor-pointer transition
          ${
            formData.reasons.includes(reason)
              ? "border-black"
              : "border-gray-300"
          }`}
                  >
                    <p className="text-sm">{reason}</p>

                    {/* RADIO */}
                    <div
                      className={`h-5 w-5 rounded-full border flex items-center justify-center
            ${
              formData.reasons.includes(reason)
                ? "border-black"
                : "border-gray-400"
            }`}
                    >
                      {formData.reasons.includes(reason) && (
                        <Check className="h-3 w-3 text-black" />
                      )}
                    </div>
                  </div>
                ))}
            </div>
            <div
              onClick={() =>
                setFormData({
                  ...formData,
                  reasons: ["Other"],
                })
              }
              className={`flex items-center justify-between border mt-4 rounded-lg p-4 cursor-pointer transition
    ${formData.reasons.includes("Other") ? "border-black" : "border-gray-300"}`}
            >
              <p className="text-sm">Other</p>

              <div
                className={`h-5 w-5 rounded-full border flex items-center justify-center
      ${
        formData.reasons.includes("Other") ? "border-black" : "border-gray-400"
      }`}
              >
                {formData.reasons.includes("Other") && (
                  <div className="h-2.5 w-2.5 bg-black rounded-full" />
                )}
              </div>
            </div>

            {formData.reasons.includes("Other") && (
              <Input
                className="mt-3 h-10 text-sm"
                placeholder="Please specify reason"
                value={formData.otherReason}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    otherReason: e.target.value,
                  })
                }
              />
            )}

            <Button
              className="w-full mt-6 h-10 text-sm"
              onClick={() => setStep(Step.Confirm)}
            >
              Continue
            </Button>
          </CardContent>
        );

      // ======================
      // CONFIRM
      // ======================
      case Step.Confirm:
        const is_Permanent = formData.unsubscribeType === "permanent";

        return (
          <CardContent className="px-6 pb-8 mt-6">
            {/* INFO BOX */}
            <div className="bg-[#E8F1F8] border border-blue-200 rounded-lg p-4 flex gap-3">
              {/* LEFT ICON */}
              <div className="mt-1">
                {is_Permanent ? (
                  <CircleX className="h-6 w-6 text-gray-600" />
                ) : (
                  <CirclePause className="h-6 w-6 text-gray-600" />
                )}
              </div>

              {/* TEXT */}
              <div>
                <p className="text-sm text-left font-medium">
                  {is_Permanent
                    ? "Unsubscribe permanently?"
                    : "Stop SMS updates for this trip?"}
                </p>

                <p className="text-xs text-left text-gray-600 mt-1 leading-relaxed">
                  {is_Permanent
                    ? "You won’t receive any future travel SMS alerts."
                    : "You won't receive further SMS updates for this trip. Future trips will still notify you."}
                </p>
              </div>
            </div>

            <Button className="w-full mt-6 h-11" onClick={confirmUnsubscribe}>
              Confirm unsubscribe
            </Button>

            <button
              className="w-full text-sm text-gray-500 mt-4"
              onClick={back}
            >
              Go back
            </button>
          </CardContent>
        );

      // ======================
      // SUCCESS (CENTERED)
      // ======================
      case Step.Success:
        const isPermanent =
          optoutTypeFromServer === "permanent" ||
          formData.unsubscribeType === "permanent";

        return (
          <CardContent className="px-6 pb-8 mt-6 text-center">
            {/* GREEN ROUND ICON */}
            <div className="flex justify-center mb-5">
              <div className="h-10 w-10 rounded-full border border-green-500 bg-green-50 flex items-center justify-center">
                <Check className="h-5 w-5 text-green-600" />
              </div>
            </div>

            {/* TITLE */}
            <CardTitle className="text-lg font-semibold">
              {isPermanent
                ? "You have been unsubscribed permanently"
                : "Notifications paused for this trip"}
            </CardTitle>

            {/* DESCRIPTION */}
            <CardDescription className="text-sm text-gray-500 mt-3 leading-relaxed">
              {isPermanent
                ? "You will no longer receive any SMS notifications from this service."
                : "You won't receive further SMS updates for this trip. Future trips will notify you automatically."}
            </CardDescription>
          </CardContent>
        );

      case Step.InvalidLink:
        return (
          <CardContent className="text-center py-8 text-sm text-red-500">
            Invalid or expired link
          </CardContent>
        );
    }
  };
  // ==========================
  // MAIN UI
  // ==========================

  return (
    <div className="flex justify-center p-6">
      <Card className="w-full max-w-md">
        {step === Step.Intro && (
          <CardHeader className="flex flex-row items-center justify-center gap-2 ">
            <Globe className="h-6 w-6 text-blue-600" />

            <span className="text-xs font-semibold tracking-wide text-gray-500 pb-2">
              TRAVEL ALERTS
            </span>
          </CardHeader>
        )}

        {renderStep()}
      </Card>
    </div>
  );
};

export default Unsubscribe;
